import React_Preact from 'react'
import ReactDOM_Preact from 'react-dom/client'
import { createPortal } from 'react-dom'

import { setCookie, getCookie } from './utilities'
window.setCookie = setCookie
window.getCookie = getCookie

const isWordPressEditor = window.location.pathname.indexOf('/wp-admin/') > -1 && 
	(typeof window.wp !== 'undefined' || typeof window.React !== 'undefined')

// We import the React stuff we need, but only apply it to the window if we're not in the editor. It conflicts with the WordPress editor copy of React. (It's actually Preact so it's more svelte on the user-facing site.)
if (!isWordPressEditor || typeof window.React === 'undefined') {
	window.React = React_Preact
	window.ReactDOM = ReactDOM_Preact
	if (!window.ReactDOM.createPortal) {
		window.ReactDOM.createPortal = createPortal
	}
} else {
	if (window.ReactDOM && !window.ReactDOM.createPortal) {
		window.ReactDOM.createPortal = createPortal
	}
}

const distPath = '/wp-content/themes/parasoftsite/react/dist'

// Component bundle paths - dynamically loaded instead of static imports
const componentBundlePaths = {
	Footer: `${distPath}/footer.bundle.js`,
	TabbedCards: `${distPath}/tabbed_cards.bundle.js`,
	ProductsSlider: `${distPath}/products_slider.bundle.js`,
	Statistics: `${distPath}/statistics.bundle.js`,
	IndustriesSlider: `${distPath}/industries_slider.bundle.js`,
	Accordion: `${distPath}/accordion.bundle.js`,
	SubscribeForm: `${distPath}/subscribe_form.bundle.js`,
	Resources: `${distPath}/resources.bundle.js`,
	FeaturedIntegrations: `${distPath}/featured_integrations.bundle.js`,
	Modal: `${distPath}/modal.bundle.js`,
	VideoModal: `${distPath}/video_modal.bundle.js`,
	Leadership: `${distPath}/leadership.bundle.js`,
	TestimonialsSlider: `${distPath}/testimonials_slider.bundle.js`,
	ResourcesSearch: `${distPath}/resources_search.bundle.js`,
	ImageModal: `${distPath}/image_modal.bundle.js`,
	ProductCards: `${distPath}/product_cards.bundle.js`,
	Webinars: `${distPath}/webinars.bundle.js`,
	Testimonials: `${distPath}/testimonials.bundle.js`,
	ToolIntegrations: `${distPath}/tool_integrations.bundle.js`,
	TryParasoft: `${distPath}/try_parasoft.bundle.js`,
	Glossary: `${distPath}/glossary.bundle.js`,
	NewsAndEvents: `${distPath}/news_and_events.bundle.js`,
	SiteSearch: `${distPath}/site_search.bundle.js`,
	SubscribeModal: `${distPath}/subscribe_modal.bundle.js`,
	LearningCenter: `${distPath}/learning_center.bundle.js`,
	FormBlock: `${distPath}/form_block.bundle.js`,
	TechnologyPartners: `${distPath}/technology_partners.bundle.js`,
	AccordionAnimation: `${distPath}/accordion_animation.bundle.js`,
	AccordionRightImage: `${distPath}/accordion_right_image.bundle.js`,
	UseCaseSlider: `${distPath}/use_case_slider.bundle.js`,
	ProductVideoSlider: `${distPath}/product_video_slider.bundle.js`,
}

const componentCache = {}

function getLibraryName(componentName) {
	const snakeCase = componentName.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '')
	return snakeCase + 'Component'
}

async function loadComponent(componentName, index) {
	// Check cache first
	if (componentCache[componentName]) {
		return componentCache[componentName]
	}

	const bundlePath = componentBundlePaths[componentName]
	if (!bundlePath) {
		return null
	}

	return new Promise((resolve, reject) => {
		const existingScript = document.querySelector(`script[data-component="${componentName}"]`)
		if (existingScript) {
			const libraryName = getLibraryName(componentName)
			const Component = window[libraryName]?.default || window[libraryName]
			if (Component) {
				componentCache[componentName] = Component
				return resolve(Component)
			}
		}

		const script = document.createElement('script')
		script.src = bundlePath + '?v=' + (window?.siteVersion ?? '0')
		script.setAttribute('data-component', componentName)
		script.async = true

		if (index <= 1) {
			script.fetchPriority = 'high'
		}
		script.onload = () => {
			setTimeout(() => {
				const libraryName = getLibraryName(componentName)
				const Component = window[libraryName]?.default || window[libraryName]
				if (Component) {
					componentCache[componentName] = Component
					resolve(Component)
				} else {
					reject(new Error(`Component ${componentName} not found`))
				}
			}, 0)
		}
		
		script.onerror = () => reject(new Error(`Failed to load ${bundlePath}`))
		document.head.appendChild(script)
	})
}

function doHydrate(component) {
	const { componentName, props, target } = component;

	try {
		const componentNameArray = componentName.split(' ')
		const capitalizedComponentNameArray = componentNameArray.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		const componentSlugName = capitalizedComponentNameArray.join('')

		// Load component dynamically instead of from static components object
		loadComponent(componentSlugName).then((Component) => {
			// Output component in a shadowroot if in wp-admin to avoid wordpress admin stylesheets messing up component rendering.
			let shadow = false
			let root
			if (window.location.pathname.indexOf('/wp-admin/') > -1) {
				shadow = true
				let shadowRoot = target.shadowRoot
				if (!shadowRoot) {
					shadowRoot = target.attachShadow({mode: 'open'})
				}
				root = ReactDOM.createRoot(shadowRoot)		
			} else {
				root = ReactDOM.createRoot(target)
			}

			if (Component) {
				root.render(
					process.env.NODE_ENV === 'development' ? (
						<React.StrictMode>
							{shadow && <link rel="stylesheet" href="/wp-content/themes/parasoftsite/react/dist/tailwind.css" type="text/css" media="screen" />}
							<Component {...props} />
						</React.StrictMode>
					) : (
						<>
							{shadow && <link rel="stylesheet" href="/wp-content/themes/parasoftsite/react/dist/tailwind.css" type="text/css" media="screen" />}
							<Component {...props} />
						</>
					)
				)
			} else {
				root.render(
					process.env.NODE_ENV === 'development' ? (
						<React.StrictMode>
							{shadow && <link rel="stylesheet" href="/wp-content/themes/parasoftsite/react/dist/tailwind.css" type="text/css" media="screen" />}
							<p className="text-red-500">Component {componentName} not found. See hydrate.js components library.</p>
						</React.StrictMode>
					) : (
						<>
							{shadow && <link rel="stylesheet" href="/wp-content/themes/parasoftsite/react/dist/tailwind.css" type="text/css" media="screen" />}
							<p className="text-red-500">Component {componentName} not found. See hydrate.js components library.</p>
						</>
					)
				)
			}
		}).catch((error) => {
			console.error('Error loading component', error)
		})
	} catch (error) {
		console.error('Error hydrating component', error)
	}
}

async function hydrateComponentsAsync(componentsToHydrate) {
	// Sort components so deferred ones are last
	const sortedComponents = [...componentsToHydrate].sort((a, b) => {
		const aDefer = a.defer || false
		const bDefer = b.defer || false
		
		if (aDefer && !bDefer) return 1
		if (!aDefer && bDefer) return -1
		return 0
	})

	const uniqueComponents = [...new Set(sortedComponents.map(c => {
		const componentNameArray = c.componentName.split(' ')
		return componentNameArray.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('')
	}))]

	await Promise.all(uniqueComponents.map((name, index) => loadComponent(name, index).catch(() => null)))

	for (const component of sortedComponents) {
		try {
			doHydrate(component)
		} catch (error) {
			console.error(`Error hydrating component "${component.componentName}"`, error)
		}
		await new Promise(r => setTimeout(r, 0))
	}

	window.postMessage({message: 'componentsHydrated'}, '*')
}

window.hydrateComponents = function(componentsToHydrate) {
	hydrateComponentsAsync(componentsToHydrate)
}

window.hydrateComponentImmediately = async function(componentName, props, target) {
	const componentNameArray = componentName.split(' ')
	const capitalizedComponentNameArray = componentNameArray.map(word => word.charAt(0).toUpperCase() + word.slice(1))
	const componentSlugName = capitalizedComponentNameArray.join('')
	
	const Component = await loadComponent(componentSlugName)
	if (Component) {
		doHydrate({componentName, props, target})
	}
}